'use client';

import { useState } from 'react';
import { useStore } from 'zustand';

import {
  CART_COUPON_GENERIC_ERROR_MESSAGE,
  CART_COUPON_REMOVE_ERROR_MESSAGE,
  CART_COUPON_SYNC_ERROR_MESSAGE,
  COUPON_NOT_APPLICABLE_BACKEND_MESSAGE,
} from '@/app/(main)/_components/cart/cart-coupon/cart-coupon.const';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { CartCouponSyncError } from '@/store/cart/cart-coupon-sync.error';
import {
  applyCouponToCart,
  cartStore,
  dismissCouponNotice,
  raiseCouponNotice,
  removeCouponFromCart,
} from '@/store/cart/cart.store';
import { CartCouponNotice, CartItem } from '@/types/cart.types';
import { trackCouponError } from '@/utils/cart/cart-analytics.util';
import { getCouponAppliedItemCount } from '@/utils/cart/get-cart-total.util';
import { HttpException } from '@/utils/api/api-client';

const logger = getLogger('cart.coupon');

export interface UseCartCouponResult {
  appliedCode: string | null;
  appliedItemCount: number;
  totalItemCount: number;
  notice: CartCouponNotice | null;
  isApplying: boolean;
  isRemoving: boolean;
  errorMessage: string | null;
  apply: (code: string) => Promise<boolean>;
  remove: () => Promise<boolean>;
  clearError: () => void;
  dismissNotice: () => void;
}

export function readCouponErrorMessage(error: unknown): string {
  if (error instanceof CartCouponSyncError) {
    return CART_COUPON_SYNC_ERROR_MESSAGE;
  }

  if (!(error instanceof HttpException)) {
    return CART_COUPON_GENERIC_ERROR_MESSAGE;
  }

  const message = (error.data as { message?: unknown } | undefined)?.message;

  return typeof message === 'string' && message.length > 0
    ? message
    : CART_COUPON_GENERIC_ERROR_MESSAGE;
}

/**
 * An item is promotional by carrying a `promotionId`, which is what the backend
 * refuses a coupon on — the cart may still show it at full price, because a
 * promotion whose price matches the original discounts nothing. The message is
 * the only one that explains such a refusal, so it is preferred over the
 * backend's vaguer wording even when the promotion is invisible on screen.
 */
function isPromotionalOnlyRejection(message: string, items: CartItem[]): boolean {
  return message === COUPON_NOT_APPLICABLE_BACKEND_MESSAGE
    && items.length > 0
    && items.every((item) => Boolean(item.promotionId));
}

export function useCartCoupon(): UseCartCouponResult {
  const appliedCode = useStore(cartStore, (state) => state.cart?.coupon?.code ?? null);
  const notice = useStore(cartStore, (state) => state.couponNotice);
  const items = useStore(cartStore, (state) => state.cart?.items);

  const { toast } = useToast();
  const [isApplying, setIsApplying] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function apply(code: string): Promise<boolean> {
    setIsApplying(true);
    setErrorMessage(null);

    try {
      await applyCouponToCart(code);

      return true;
    } catch (error) {
      const message = readCouponErrorMessage(error);

      logger.error('Failed to apply the coupon to the cart', {
        cart: { event: 'coupon_apply_failed', coupon_code: code },
        error,
      });
      trackCouponError(code, message);

      if (isPromotionalOnlyRejection(message, items ?? [])) {
        raiseCouponNotice('promotional');
      } else {
        setErrorMessage(message);
      }

      return false;
    } finally {
      setIsApplying(false);
    }
  }

  async function remove(): Promise<boolean> {
    setIsRemoving(true);

    try {
      await removeCouponFromCart();
      setErrorMessage(null);

      return true;
    } catch (error) {
      logger.error('Failed to remove the coupon from the cart', {
        cart: { event: 'coupon_remove_failed' },
        error,
      });
      toast.error(CART_COUPON_REMOVE_ERROR_MESSAGE);

      return false;
    } finally {
      setIsRemoving(false);
    }
  }

  return {
    appliedCode,
    appliedItemCount: getCouponAppliedItemCount(items ?? []),
    totalItemCount: items?.length ?? 0,
    notice,
    isApplying,
    isRemoving,
    errorMessage,
    apply,
    remove,
    clearError: () => setErrorMessage(null),
    dismissNotice: dismissCouponNotice,
  };
}
